import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ShieldCheck, AlertCircle, CheckCircle2, Receipt } from "lucide-react";
import { toast } from "sonner";

function formatCurrency(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MembershipPanel({ m, onCheckout, onCancel, onResume, busy }) {
  if (m.active) {
    const willCancel = m.cancel_at_period_end;
    const autoRenew = m.auto_renew;
    return (
      <Card className="border-[color:var(--bright-green)]/30 bg-[color:var(--bright-green)]/5" data-testid="billing-membership-active">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[color:var(--bright-green)]" />
              <strong>Membership active</strong>
            </div>
            <Badge className="bg-[color:var(--bright-green)] text-white" data-testid="billing-membership-status-badge">Active</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                {willCancel ? "Ends" : autoRenew ? "Auto-renews" : "Expires"}
              </div>
              <div className="font-medium" data-testid="billing-membership-expires">
                {m.expires_at ? new Date(m.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">Days remaining</div>
              <div className="font-medium" data-testid="billing-membership-days">
                {m.days_remaining ?? "—"} days
              </div>
            </div>
          </div>
          {willCancel && (
            <div className="mt-3 flex items-start gap-2 text-xs text-[color:var(--orange)]" data-testid="billing-membership-canceling">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Auto-renewal is OFF. Your membership will end on the date above unless you resume.</span>
            </div>
          )}
          {!willCancel && autoRenew && (
            <p className="mt-3 text-[10px] text-[color:var(--text-secondary)]" data-testid="billing-membership-autorenew-note">
              Auto-renewing yearly at {formatCurrency(m.fee_usd)} via Stripe. Cancel anytime — you&apos;ll
              keep access until the renewal date.
            </p>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            {autoRenew && !willCancel && (
              <Button
                onClick={onCancel}
                disabled={busy === "cancel"}
                variant="outline"
                className="border-black/10 w-full sm:w-auto"
                data-testid="billing-membership-cancel-autorenew-btn"
              >
                {busy === "cancel" ? "Updating…" : "Turn off auto-renew"}
              </Button>
            )}
            {willCancel && (
              <Button
                onClick={onResume}
                disabled={busy === "resume"}
                className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white w-full sm:w-auto"
                data-testid="billing-membership-resume-autorenew-btn"
              >
                {busy === "resume" ? "Updating…" : "Resume auto-renewal"}
              </Button>
            )}
            {!autoRenew && (
              <Button
                onClick={() => onCheckout("membership")}
                disabled={busy === "membership"}
                className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white w-full"
                data-testid="billing-membership-renew-btn"
              >
                {busy === "membership" ? "Redirecting…" : `Activate auto-renew for ${formatCurrency(m.fee_usd)}/yr`}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-[color:var(--fire-red)]/30 bg-[color:var(--fire-red)]/5" data-testid="billing-membership-inactive">
      <CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[color:var(--fire-red)]" />
            <strong>Membership required</strong>
          </div>
          <Badge variant="outline" className="border-[color:var(--fire-red)]/40 text-[color:var(--fire-red)]" data-testid="billing-membership-status-badge">Inactive</Badge>
        </div>
        <p className="text-sm text-[color:var(--text-secondary)] mt-3">
          Activate your yearly Guild membership to unlock the 60-day counterclaim process,
          trustee pledge, and good-accounting-ledger audit.{" "}
          <strong className="text-[color:var(--text-primary)]">Auto-renews yearly</strong> — cancel anytime.
        </p>
        <div className="font-serif text-3xl mt-3" data-testid="billing-membership-fee">
          {formatCurrency(m.fee_usd)}<span className="text-sm font-sans text-[color:var(--text-secondary)] ml-2">/ year</span>
        </div>
        <Button
          onClick={() => onCheckout("membership")}
          disabled={busy === "membership"}
          className="mt-4 w-full bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white"
          data-testid="billing-membership-pay-btn"
        >
          {busy === "membership" ? "Redirecting…" : "Activate auto-renewing membership"}
        </Button>
      </CardContent>
    </Card>
  );
}

function CounterclaimPanel({ c, onCheckout, busy }) {
  if (!c.has_application) {
    return (
      <Card className="border-black/5" data-testid="billing-counterclaim-no-app">
        <CardContent className="p-5">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[color:var(--orange)]" />
            <strong>Counterclaim service fee</strong>
          </div>
          <p className="text-sm text-[color:var(--text-secondary)] mt-3">
            Submit your application to lock in your counterclaim service fee
            (1.5% of mortgage balance, paid only at settlement).
          </p>
          <Link to="/apply" className="mt-4 block">
            <Button variant="outline" className="border-black/10 w-full" data-testid="billing-counterclaim-apply-btn">
              Start my application
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }
  if (c.paid) {
    return (
      <Card className="border-[color:var(--bright-green)]/30 bg-[color:var(--bright-green)]/5" data-testid="billing-counterclaim-paid">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[color:var(--bright-green)]" />
              <strong>Counterclaim service fee paid</strong>
            </div>
            <Badge className="bg-[color:var(--bright-green)] text-white">Paid</Badge>
          </div>
          <div className="font-serif text-3xl mt-3" data-testid="billing-counterclaim-amount">{formatCurrency(c.calculated_fee_usd)}</div>
          <p className="text-xs text-[color:var(--text-secondary)] mt-2">
            Calculated as 1.5% × {formatCurrency(c.mortgage_balance)} mortgage balance.
            Paid on {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : "—"}.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-black/5" data-testid="billing-counterclaim-due">
      <CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[color:var(--orange)]" />
            <strong>Counterclaim service fee</strong>
          </div>
          <Badge variant="outline" className="border-black/10 text-xs">Due at settlement</Badge>
        </div>
        <div className="font-serif text-3xl mt-3" data-testid="billing-counterclaim-amount">{formatCurrency(c.calculated_fee_usd)}</div>
        <p className="text-xs text-[color:var(--text-secondary)] mt-2">
          Calculated as {c.fee_percent}% × {formatCurrency(c.mortgage_balance)} mortgage balance from your application.
          Payable at settlement.{" "}
          <Link to="/accounting" className="text-[color:var(--fire-red)] hover:underline">See breakdown →</Link>
        </p>
        <Button
          onClick={() => onCheckout("mortgage_fee")}
          disabled={busy}
          className="mt-4 w-full bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white"
          data-testid="billing-counterclaim-pay-btn"
        >
          {busy === "mortgage_fee" ? "Redirecting…" : `Pay ${formatCurrency(c.calculated_fee_usd)} now`}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function BillingCard() {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/payments/me/billing");
      setBilling(r.data);
    } catch (e) { toast.error(getApiError(e)); }
  };

  useEffect(() => { refresh(); }, []);

  const checkout = async (pkg) => {
    setBusy(pkg);
    try {
      const r = await api.post("/payments/checkout", {
        package: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(getApiError(e));
      setBusy(false);
    }
  };

  const cancelAutoRenew = async () => {
    if (!window.confirm("Turn off auto-renewal? You'll keep access until your current period ends, then your membership will end.")) return;
    setBusy("cancel");
    try {
      await api.post("/payments/subscription/cancel");
      toast.success("Auto-renewal turned off");
      await refresh();
    } catch (e) { toast.error(getApiError(e)); }
    finally { setBusy(false); }
  };

  const resumeAutoRenew = async () => {
    setBusy("resume");
    try {
      await api.post("/payments/subscription/resume");
      toast.success("Auto-renewal resumed");
      await refresh();
    } catch (e) { toast.error(getApiError(e)); }
    finally { setBusy(false); }
  };

  if (!billing) return null;

  return (
    <Card className="border-black/5" data-testid="billing-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-5 w-5 text-[color:var(--orange)]" />
          <h2 className="font-serif text-2xl">Billing &amp; membership</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <MembershipPanel m={billing.membership} onCheckout={checkout} onCancel={cancelAutoRenew} onResume={resumeAutoRenew} busy={busy} />
          <CounterclaimPanel c={billing.counterclaim} onCheckout={checkout} busy={busy} />
        </div>
      </CardContent>
    </Card>
  );
}
